import { useEffect, useRef, useState } from "react";
import {
    FaSatelliteDish,
    FaAtom,
    FaWaveSquare,
    FaProjectDiagram,
    FaCogs,
    FaMicrochip,
    FaIndustry,
    FaBolt,
    FaPlug,
    FaBrain,
    FaChevronDown,
} from "react-icons/fa";

import styles from "./TopicsPage.module.css";

const TOPICS = [
    {
        icon: <FaSatelliteDish />,
        title: "Traveling-Wave Tube Amplifiers",
        subtitle: "Advances from Space Applications to Terahertz Frequencies",
        points: [
            "Traveling-wave tubes (all types)",
            "Millimeter-wave and THz amplifiers",
            "Spatially distributed electron beam devices",
            "Pulse compression devices",
        ],
    },
    {
        icon: <FaAtom />,
        title: "Fast-Wave Devices",
        subtitle: "Fusion Energy and High-Power Microwave Systems",
        points: [
            "Gyrotrons, gyro-amplifiers, and fast-wave oscillators",
            "Free-electron lasers and masers",
            "Plasma-filled amplifiers and oscillators",
        ],
    },
    {
        icon: <FaWaveSquare />,
        title: "High-Efficiency Klystrons",
        subtitle: "Klystrons and Inductive Output Tubes",
        points: [
            "Multi-beam, compact, and high-efficiency klystrons",
            "Inductive output tubes (IOTs)",
            "Triodes, tetrodes, and pentodes",
        ],
    },
    {
        icon: <FaProjectDiagram />,
        title: "Electron Sources",
        subtitle: "Thermionic, Field, and Photoemission Technologies",
        points: [
            "Thermionic emitters including Scandates and advanced cathodes",
            "Non-thermionic emitters and photocathodes",
            "Field emitters and arrays using CNTs and nanostructures",
            "Cathode design, fabrication, and characterization",
            "Accelerator emission physics and beam quality",
        ],
    },
    {
        icon: <FaCogs />,
        title: "Modeling & Simulation",
        subtitle: "Design, Verification, and Optimization of VEDs",
        points: [
            "Large-signal and particle-in-cell modeling",
            "Electromagnetic solvers",
            "Adjoint methods for optimization",
            "Verification and validation strategies",
        ],
    },
    {
        icon: <FaMicrochip />,
        title: "Microfabrication & THz Devices",
        subtitle: "Next-Generation Miniaturized Vacuum Electronics",
        points: [
            "Backward-wave oscillators and clinotrons",
            "Smith-Purcell devices",
            "Metamaterials and photonic THz structures",
            "Miniaturization of vacuum devices",
            "Novel measurement techniques and diagnostics",
        ],
    },
    {
        icon: <FaIndustry />,
        title: "Advanced Manufacturing",
        subtitle: "Materials and Quality Assurance for VEDs",
        points: [
            "Additive and subtractive manufacturing methods",
            "Novel dielectric, coating, and magnetic materials",
            "Surface processing and electropolishing",
            "Brazing and joining technologies",
            "In-situ diagnostics and quality assurance",
            "Thermal management and magnet technologies",
            "Reliability engineering",
        ],
    },
    {
        icon: <FaBolt />,
        title: "High-Power Microwaves",
        subtitle: "Advanced Sources, Systems, and Electromagnetic Effects",
        points: [
            "High-power microwave devices",
            "Relativistic microwave sources",
            "RF directed energy devices and systems",
            "Amplifier-antenna integration",
            "System survivability and hardening",
            "RF interference and electromagnetic effects",
            "Magnetrons and crossed-field devices",
        ],
    },
    {
        icon: <FaPlug />,
        title: "Supporting Subsystems",
        subtitle: "Power Supplies and System Integration",
        points: [
            "Transmitters and power modules",
            "Electronic power conditioners",
            "Modulators and power supplies",
            "Linearizers and control systems",
            "RF windows and collectors",
            "Subsystem integration",
            "Reliability and qualification methods",
        ],
    },
    {
        icon: <FaBrain />,
        title: "AI / ML for VEDs",
        subtitle: "Intelligent Design, Modeling, and Optimization",
        points: [
            "AI-driven electron source optimization",
            "Passive component optimization",
            "Transmission-line design",
            "Predictive modeling",
            "Machine-learning-assisted simulation",
        ],
    },
];

const TopicCard = ({ topic, index, isOpen, onToggle }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;

        if (!node) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.12,
            }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    const number = String(index + 1).padStart(2, "0");

    return (
        <article
            ref={ref}
            className={[
                styles.topicPanel,
                visible ? styles.topicPanelVisible : "",
                isOpen ? styles.topicPanelOpen : "",
            ].join(" ")}
            style={{ "--i": index }}
        >
            <button
                type="button"
                className={styles.topicPanelHeader}
                onClick={() => onToggle(index)}
                aria-expanded={isOpen}
            >
                <div className={styles.topicNumber}>
                    <span>TOPIC</span>
                    <strong>{number}</strong>
                </div>

                <div className={styles.topicIconWrap}>
                    {topic.icon}
                </div>

                <div className={styles.topicHeading}>
                    <h2 className={styles.topicPanelTitle}>
                        {topic.title}
                    </h2>

                    <p className={styles.topicSubtitle}>
                        {topic.subtitle}
                    </p>
                </div>

                <div className={styles.topicMeta}>
                    <span className={styles.topicCount}>
                        {topic.points.length} Areas
                    </span>

                    <span className={styles.topicChevronWrap}>
                        <FaChevronDown
                            className={styles.topicChevron}
                        />
                    </span>
                </div>
            </button>

            <div className={styles.topicPanelBody}>
                <div className={styles.topicPanelBodyInner}>
                    <div className={styles.bodyDivider} />

                    <ul className={styles.topicList}>
                        {topic.points.map((point, i) => (
                            <li
                                key={i}
                                style={{ "--item": i }}
                            >
                                <span className={styles.pointMarker}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>

                                <span>{point}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </article>
    );
};

const TopicsPage = () => {
    const [openSet, setOpenSet] = useState(() => new Set([0]));

    const toggle = (index) => {
        setOpenSet((previous) => {
            const next = new Set(previous);

            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }

            return next;
        });
    };

    return (
        <main className={styles.page}>
            
            {/* HERO */}
            <section className={styles.hero}>
                <div className={styles.heroInner}>
                    <div className={styles.heroBadge}>
                        <span className={styles.badgeDot} />
                        Call for Papers 2026
                    </div>

                    <span className={styles.heroEyebrow}>
                        Vacuum Electronics Research
                    </span>

                    <h1 className={styles.heroTitle}>
                        Conference
                        <span> Topics</span>
                    </h1>

                    <p className={styles.heroTagline}>
                        Explore ten research channels spanning advanced
                        vacuum electronics, high-power systems,
                        terahertz technologies, intelligent design,
                        and next-generation electron devices.
                    </p>

                    <div className={styles.heroStats}>
                        <div>
                            <strong>10</strong>
                            <span>Research Topics</span>
                        </div>

                        <div>
                            <strong>50+</strong>
                            <span>Focus Areas</span>
                        </div>

                        <div>
                            <strong>VED</strong>
                            <span>Innovation</span>
                        </div>
                    </div>

                    <div className={styles.scrollHint}>
                        <span>Explore Topics</span>
                        <div className={styles.mouse}>
                            <span />
                        </div>
                    </div>
                </div>
            </section>

            {/* TOPICS */}
            <section className={styles.contentSection}>
                <div className={styles.sectionIntro}>
                    <span className={styles.sectionLabel}>
                        Research Channels
                    </span>

                    <h2>
                        Find Your <span>Research Area</span>
                    </h2>

                    <p>
                        Select a topic to explore the suggested research
                        areas and identify the best category for your
                        submission.
                    </p>
                </div>

                <div className={styles.grid}>
                    {TOPICS.map((topic, index) => (
                        <TopicCard
                            key={index}
                            topic={topic}
                            index={index}
                            isOpen={openSet.has(index)}
                            onToggle={toggle}
                        />
                    ))}
                </div>
            </section>
        </main>
    );
};

export default TopicsPage;